import { tallySync as tallySyncClient, unwrap } from "../api";
import { formatMoment as formatIstMoment } from "../format";
import type { TallySyncComponents } from "@steelyard/api-client";
import { isBr07Defect, type InvoiceRow } from "./invoicing";
import { isGuid, redirectToCanonical, syncItemPath } from "../paths";

/**
 * Ported from apps/admin/lib/api.ts's `tallySync()`/`tallySyncWritable()` client (paths only —
 * the render/writable split doesn't apply here, every call goes through the same BFF-proxied
 * `api` helper) and apps/admin/app/(app)/integrations/tally/tally.ts.
 *
 * The agent's own inbound surface (`/agent/heartbeat`, `/agent/work`, `/agent/result`,
 * `/agent/customers`, `/agent/receipts`) is outbound-only from the connector agent by design
 * (D-17) and must never be called from here — nothing in this file touches it.
 */

// ---- Agent status + config: the OpenAPI document declares these response bodies, so they're
// plain types here rather than runtime-parsed (GAP-4 is closed for these two operations).

export type AgentState = "alive" | "silent" | "auth_failing" | "not_configured";

export type AgentStatus = {
  state: AgentState;
  alertRaised: boolean;
  lastHeartbeatAt: string | null;
  companyName: string | null;
  tallyVersionReported: string | null;
  agentInstance: string | null;
  consecutiveAuthFailures: number;
  withinBusinessHours: boolean;
  /** The agent runs its stub gateway: it reports success but posts nothing to Tally (D-20). */
  stubbed?: boolean;
};

export type TallyConfig = {
  i05Confirmed: boolean;
  companyName: string;
  ledgerGroup: string;
  perTierSubGroups: Record<string, string>;
  voucherTypes: Record<string, string>;
  customerPullSchedule: string;
  receiptPullSchedule: string;
  heartbeatAlertPeriod: string;
  heartbeatPollInterval: string;
  leaseDuration: string;
  retryBackoffBase: string;
  retryBackoffCap: string;
  authFailureAlertThreshold: number;
  businessHoursStart: string;
  businessHoursEnd: string;
  businessDays: string[];
  auditRetentionYears: number;
};

/** "not_configured" → "Not configured" (PAK-07: plain language for an enum the API sends raw). */
export function agentStateLabel(state: string): string {
  const words = state.replaceAll("_", " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * "00:30:00" → "30 minutes", "01:00:00" → "1 hour", "1.00:00:00" → "1 day" (PAK-07). A .NET
 * `TimeSpan` on the wire, always `[d.]hh:mm:ss` — parsed rather than reformatted via `Date`,
 * which has no duration type of its own.
 */
export function durationLabel(value: string): string {
  const match = /^(?:(\d+)\.)?(\d{1,2}):(\d{2}):(\d{2})(?:\.\d+)?$/.exec(value.trim());
  if (!match) return value;
  const [, d, h, m, s] = match;
  const totalMinutes = Number(d ?? 0) * 1440 + Number(h) * 60 + Number(m);
  const totalSeconds = Number(s);

  if (totalMinutes === 0) return totalSeconds === 1 ? "1 second" : `${totalSeconds} seconds`;
  if (totalMinutes % 1440 === 0) {
    const days = totalMinutes / 1440;
    return days === 1 ? "1 day" : `${days} days`;
  }
  if (totalMinutes % 60 === 0) {
    const hours = totalMinutes / 60;
    return hours === 1 ? "1 hour" : `${hours} hours`;
  }
  return totalMinutes === 1 ? "1 minute" : `${totalMinutes} minutes`;
}

/**
 * "09:00:00" → "9:00 AM" (PAK-07). A clock time rather than a duration — reuses `durationLabel`'s
 * same `hh:mm:ss` shape, just formatted as a time of day.
 */
export function clockTimeLabel(value: string): string {
  const match = /^(\d{1,2}):(\d{2}):(\d{2})/.exec(value.trim());
  if (!match) return value;
  const hour24 = Number(match[1]);
  const period = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}:${match[2]} ${period}`;
}

/** "creditNote" → "Credit Note" (PAK-07: a voucher-type map's keys are wire identifiers, not labels). */
export function camelCaseLabel(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());
}

/**
 * The handful of cron expressions this schedule ever actually sends, in plain words (PAK-07).
 * An expression outside this set is shown verbatim rather than guessed at — a wrong guess about
 * a schedule is worse than an admin reading raw cron.
 */
export function cronLabel(cron: string): string {
  const trimmed = cron.trim();
  const everyNHours = /^0 \*\/(\d+) \* \* \*$/.exec(trimmed);
  if (everyNHours) return Number(everyNHours[1]) === 1 ? "every hour" : `every ${everyNHours[1]} hours`;
  const everyNMinutes = /^\*\/(\d+) \* \* \* \*$/.exec(trimmed);
  if (everyNMinutes) return `every ${everyNMinutes[1]} minutes`;
  if (trimmed === "0 0 * * *") return "once a day, at midnight";
  return trimmed;
}

export async function getAgentStatus() {
  return unwrap((await tallySyncClient()).GET("/api/tallysync/admin/agent")) as Promise<AgentStatus>;
}

export async function getTallyConfig() {
  return unwrap((await tallySyncClient()).GET("/api/tallysync/admin/tally-config")) as Promise<TallyConfig>;
}

// ---- Dedupe (T026, FR-021/FR-027): GetTallyDedupeReport is NOT declared (GAP-4 stays open here —
// `content?: never`), so the body is handed back unparsed, same as customers.ts's getDedupe(); the
// real parsing (into the shared DedupeGroup shape apps/admin/src/components/customers/customer-dedupe-report.tsx
// merges both sources into) lives beside that shared shape, not here.
//
// `includeResolved` omitted (undefined) rather than passed as `false` so the querystring carries
// no parameter at all — the server's own default is already `false`, and this is what makes a
// resolved collision drop off on reload without this file re-deriving that rule.
export async function getTallyDedupe(includeResolved?: boolean) {
  return unwrap(
    (await tallySyncClient()).GET("/api/tallysync/admin/dedupe", {
      params: { query: { includeResolved } },
    }),
  );
}

// RotateAgentKey types both the 200 and 422 responses as `content?: never` — the controller has no
// `[ProducesResponseType(typeof(...))]` on the 200, unlike GetAgentStatus right above it — even
// though the handler really does return `{ agentKey, shownOnce }`. Runtime-parsed for the same
// reason as GetSyncQueue below (GAP-4): a cast would keep asserting a shape after the backend moved.
export type RotateKeyResult = { agentKey: string; shownOnce: boolean };

export function parseRotateKeyResult(raw: unknown): RotateKeyResult {
  const row = (raw ?? {}) as Record<string, unknown>;
  if (typeof row.agentKey !== "string" || row.agentKey.length === 0) {
    throw new Error(
      "POST /api/tallysync/admin/agent/rotate-key answered without a string agentKey. The key is " +
        "shown once and never again — showing a rotation as successful without the key would strand it.",
    );
  }
  return { agentKey: row.agentKey, shownOnce: row.shownOnce === true };
}

export async function rotateAgentKey() {
  return parseRotateKeyResult(await unwrap((await tallySyncClient()).POST("/api/tallysync/admin/agent/rotate-key")));
}

// ---- Sync queue: GetSyncQueue is NOT declared (GAP-4 stays open here), so the response is
// runtime-parsed rather than cast — a cast would keep asserting a shape after the backend moved.

export type SyncQueueItem = {
  itemId: string;
  /** `SYN-000031` — the record's URL key (`syncItemPath`). Null only if TallySync omits it. */
  itemNumber: string | null;
  entityType: string;
  entityReference: string | null;
  /** The row's readable label (a customer's name, an invoice number); null when TallySync could not say. */
  entityLabel: string | null;
  status: string;
  /** Never reset by a manual retry. NaN (never 0) when the API didn't report a number. */
  attempts: number;
  lastError: string | null;
  lastAttemptAt: string | null;
  /** GAP-1, closed 2026-09-14: the row's real enqueue time. `null` only if an older backend build
   * omits it — treated the same as "can't evaluate the floor" everywhere below, never as "just queued". */
  queuedAt: string | null;
};

/** §19's 15-minute latency floor (FR-012). Not a config field anywhere in the API (backend-gaps.md
 * GAP-1's own note), so it's a constant here — same status as any other spec-stated number this app
 * displays without pretending the API sent it. */
export const LATENCY_FLOOR_MINUTES = 15;

/**
 * True once a real `queuedAt` is old enough to have missed the floor. This subtracts a real,
 * API-supplied timestamp from the current time — display arithmetic, the same kind `formatMoment`
 * already does — not a recomputation of anything the API was asked to calculate (constitution I).
 * `null`/unparsable `queuedAt` returns false: an age that cannot be evaluated is not shown as stale.
 */
export function isPastLatencyFloor(queuedAt: string | null, now: number = Date.now()): boolean {
  if (!queuedAt) return false;
  const queuedMs = new Date(queuedAt).getTime();
  if (Number.isNaN(queuedMs)) return false;
  return now - queuedMs >= LATENCY_FLOOR_MINUTES * 60_000;
}

export type SyncQueue = {
  items: SyncQueueItem[];
  unsyncedCount: number | null;
  /** §17 "failed Tally syncs": the API's own count of `failed` rows. `null` when not reported, never 0. */
  failedCount: number | null;
};

const text = (value: unknown): string | null => (typeof value === "string" ? value : null);

function parseItem(raw: unknown, index: number): SyncQueueItem {
  const row = (raw ?? {}) as Record<string, unknown>;
  const itemId = text(row.itemId);
  const status = text(row.status);
  const entityType = text(row.entityType);

  if (itemId === null || status === null || entityType === null) {
    throw new Error(
      `GET /api/tallysync/admin/sync-queue returned a row (index ${index}) without a string ` +
        "itemId, status and entityType. A queue row that cannot be identified or statused cannot " +
        "be shown, and showing the rest as if the queue were complete would be a silent failure.",
    );
  }

  return {
    itemId,
    itemNumber: text(row.itemNumber),
    entityType,
    entityReference: text(row.entityReference),
    entityLabel: text(row.entityLabel),
    status,
    attempts: typeof row.attempts === "number" ? row.attempts : Number.NaN,
    lastError: text(row.lastError),
    lastAttemptAt: text(row.lastAttemptAt),
    queuedAt: text(row.queuedAt),
  };
}

/**
 * What a queue row is called on screen: TallySync's own label, else the reference — except a
 * customer's reference, which is its id and is never shown.
 */
export function syncItemLabel(item: Pick<SyncQueueItem, "entityType" | "entityReference" | "entityLabel">): string | null {
  if (item.entityLabel) return item.entityLabel;
  return item.entityType === "customer" ? null : item.entityReference;
}

export function parseSyncQueue(body: unknown): SyncQueue {
  const raw = (body ?? {}) as Record<string, unknown>;
  if (!Array.isArray(raw.items)) {
    throw new Error(
      "GET /api/tallysync/admin/sync-queue answered without an `items` array. The queue screen " +
        "has nothing to render, and an empty table here would be indistinguishable from a " +
        "drained queue.",
    );
  }
  return {
    items: raw.items.map(parseItem),
    unsyncedCount: typeof raw.unsyncedCount === "number" ? raw.unsyncedCount : null,
    failedCount: typeof raw.failedCount === "number" ? raw.failedCount : null,
  };
}

const TAKE = 200;
/** Exported so QueueDetail.tsx's "only the most recent 500" copy names the real bound, not a second copy of it. */
export const RECORD_LOOKUP_TAKE = 500;

export async function getSyncQueue(params: { status?: string; entityType?: string; take?: number } = {}) {
  return parseSyncQueue(
    await unwrap(
      (await tallySyncClient()).GET("/api/tallysync/admin/sync-queue", {
        params: { query: { status: params.status, entityType: params.entityType, take: params.take ?? TAKE } },
      }),
    ),
  );
}

/** No by-id read route exists — reads the queue window and finds the row (honest about the limit). */
export function getSyncQueueItem(id: string) {
  return getSyncQueue({ take: RECORD_LOOKUP_TAKE }).then((queue) => queue.items.find((row) => row.itemId.toLowerCase() === id.toLowerCase()) ?? null);
}

/** `GET /admin/sync-queue/by-number/{itemNumber}` — the same row the queue lists. Unknown → 404. */
export async function getSyncQueueItemByNumber(itemNumber: string) {
  return parseItem(
    await unwrap((await tallySyncClient()).GET("/api/tallysync/admin/sync-queue/by-number/{itemNumber}", { params: { path: { itemNumber } } })),
    0,
  );
}

/**
 * The record a route's `:itemNumber` names. A legacy id is found in the queue window and redirected
 * to its number URL, query kept; one outside the window resolves to null, and the screen says so.
 */
export async function syncItemByKey(request: Request, key: string): Promise<SyncQueueItem | null> {
  if (!isGuid(key)) return getSyncQueueItemByNumber(key);
  const item = await getSyncQueueItem(key);
  if (!item?.itemNumber) return item;
  throw redirectToCanonical(request, key, syncItemPath(item.itemNumber));
}

/**
 * §17 Integrations "error log", Tally's half: failed queue items with Tally's own text, most recent
 * attempt first (`GET /api/tallysync/admin/errors`, `integrations.read`). Declared, so typed.
 */
export type SyncErrorPage = TallySyncComponents["schemas"]["PagedResultOfSyncQueueRow"];

export async function listSyncErrors(params: { page?: number; pageSize?: number } = {}): Promise<SyncErrorPage> {
  return unwrap((await tallySyncClient()).GET("/api/tallysync/admin/errors", { params: { query: params } }));
}

export async function retrySyncItem(id: string) {
  return unwrap(
    (await tallySyncClient()).POST("/api/tallysync/admin/sync-queue/{id}/retry", { params: { path: { id } } }),
  );
}

/**
 * T019 (FR-013, BR-07, `006` FR-003). `SyncQueueRow` carries no IRN of its own — the backend
 * entity resolves the payload at enqueue time and never persists the IRN outside it (see
 * contracts/backend-gaps.md GAP-1) — so a queued invoice's compliance state is cross-checked
 * against Billing's own invoice list, which already carries `irnStatus`/`tallySyncStatus` and
 * already has a tested rule for this exact defect (`isBr07Defect`, `006`).
 *
 * `"unknown"` is deliberately not `"ok"`: a reference outside the invoices window the caller
 * checked is unverified, not clean, and must not be reported as if it were (constitution VI).
 */
export type InvoiceIrnCheck = { status: "defect"; invoice: InvoiceRow } | { status: "ok" } | { status: "unknown" };

export function checkQueuedInvoiceIrn(
  item: Pick<SyncQueueItem, "entityType" | "entityReference">,
  invoicesByNumber: ReadonlyMap<string, InvoiceRow>,
): InvoiceIrnCheck | null {
  if (item.entityType !== "invoice" || !item.entityReference) return null;
  const invoice = invoicesByNumber.get(item.entityReference);
  if (!invoice) return { status: "unknown" };
  return isBr07Defect(invoice) ? { status: "defect", invoice } : { status: "ok" };
}

/** ComplianceStatusBadge's vocabulary. An unrecognised status returns null — the caller shows the API's own word. */
export function queueBadgeState(status: string): "pending" | "done" | "failed" | null {
  if (status === "pending") return "pending";
  if (status === "sent") return "done";
  if (status === "failed") return "failed";
  return null;
}

export type EmptyQueueReason = "nothing_raised" | "drained" | "filtered";

/**
 * `statusFiltered` breaks a real bug: without it, filtering the queue to `status=sent` returned a
 * page full of sent rows that this function still called "outstanding.length === 0" and reported
 * as `"filtered"` (no records matched), hiding a table that had real rows in it. The "outstanding"
 * business rule (ignore sent rows, celebrate when nothing else is left) is only the right read of
 * an empty page on the *default* view, where the queue's job is surfacing work that needs
 * attention. Once the caller has asked the server for an exact status — sent included — trust the
 * page it returned instead of re-deriving "outstanding" on top of an explicit choice.
 */
export function emptyQueueReason(queue: SyncQueue, filtered: boolean, statusFiltered: boolean): EmptyQueueReason | null {
  if (statusFiltered) return queue.items.length > 0 ? null : "filtered";

  const outstanding = queue.items.filter((item) => item.status !== "sent");
  if (queue.items.length > 0 && outstanding.length > 0) return null;
  if (filtered) return "filtered";
  return queue.items.length === 0 ? "nothing_raised" : "drained";
}

/** Absolute IST, to the minute — never relative ("14 minutes ago"), which would be a threshold this app must not evaluate. */
export function formatMoment(iso: string | null | undefined): string {
  if (!iso) return "—";
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? iso : formatIstMoment(iso);
}
